import React, { ReactNode, useCallback, useMemo } from "react";

import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "antd/es/select";
import cn from "classnames";
import { sortBy } from "lodash-es";
import { useIntl } from "react-intl";

import User, { UserRef } from "@mapmycustomers/shared/types/User";
import userDisplayName from "@mapmycustomers/shared/util/formatters/userDisplayName";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";
import {
  AvatarWithName,
  InlineSelectField,
  SelectField,
  SelectFieldProps,
} from "@mapmycustomers/ui";

import styles from "./UserSelectField.module.scss";

const DEFAULT_OPTION_KEY = -1;
const Option = Select.Option;

interface Props extends Omit<
  SelectFieldProps<User["id"]>,
  "children" | "className" | "label" | "onChange" | "options" | "value"
> {
  className?: string;
  defaultUsersOption?: boolean | string;
  hideAllUsersIcon?: boolean;
  inline?: boolean;
  label?: boolean | ReactNode;
  onChange?: (user: undefined | User) => void;
  users: User[];
  value?: User | UserRef;
}

const UserSelectField = ({
  className,
  defaultUsersOption = true,
  hideAllUsersIcon,
  inline,
  label,
  onChange,
  required,
  users,
  value,
  ...props
}: Props) => {
  const intl = useIntl();

  const handleSelectUser = useCallback(
    (userId: User["id"]) => {
      onChange?.(
        userId === DEFAULT_OPTION_KEY ? undefined : users.find((user) => user.id === userId)
      );
    },
    [onChange, users]
  );

  const sortedUsers = useMemo(() => sortBy(users, [userDisplayName]), [users]);

  const SelectComponent = inline ? InlineSelectField<User["id"]> : SelectField<User["id"]>;

  const valueRender = useCallback(
    (userId: null | undefined | User["id"]) => {
      if (!userId) {
        return "";
      }
      const user = users.find((user) => user.id === userId);
      return user ? <AvatarWithName user={user} /> : "";
    },
    [users]
  );

  return (
    <SelectComponent
      className={cn(styles.container, className)}
      dropdownMatchSelectWidth={false}
      filterOption={defaultFilterOption}
      label={
        label === false
          ? undefined
          : (label ??
            intl.formatMessage({
              id: "userSelectField.label",
              defaultMessage: "Select User",
              description: "Default label of generic User Select field",
            }))
      }
      onChange={handleSelectUser}
      required={required}
      showSearch
      value={value?.id ?? (defaultUsersOption ? DEFAULT_OPTION_KEY : undefined)}
      {...(inline ? { showFooterButtons: false, valueRender } : {})}
      {...props}
    >
      {defaultUsersOption && (
        <Option
          className={styles.allUsersOption}
          key={DEFAULT_OPTION_KEY}
          value={DEFAULT_OPTION_KEY}
        >
          {!hideAllUsersIcon && <FontAwesomeIcon className={styles.allUsersIcon} icon={faUsers} />}
          {typeof defaultUsersOption === "string"
            ? defaultUsersOption
            : intl.formatMessage({
                id: "userSelectField.allUsers",
                defaultMessage: "All Users",
                description: "All Users option name in a generic User Select field",
              })}
        </Option>
      )}
      {/* If owner/assignee on any record is `Deleted/Deactivated` */}
      {value && !users.find((user) => user.id === value.id) && (
        <Option key={value.id} value={value.id}>
          <AvatarWithName avatarClassName={styles.avatar} user={value} />
        </Option>
      )}
      {sortedUsers.map((user) => (
        <Option key={user.id} label={userDisplayName(user)} value={user.id}>
          <AvatarWithName avatarClassName={styles.avatar} user={user} />
        </Option>
      ))}
    </SelectComponent>
  );
};

export default UserSelectField;
